<template>
  <component
    :class="{
      'file-thumbnail': true,
      'file-thumbnail--fixed-aspect-ratio': props.isFixedAspectRatio || !isImageLoaded,
    }"
    :is="props.isEntireAreaClickable && props.galleryPostfix ? 'a' : 'li'"
    :data-fancybox="props.isEntireAreaClickable && props.galleryPostfix ? `gallery${props.galleryPostfix}` : null"
    :href="props.isEntireAreaClickable ? (props.fileName ? generateSrc(props.fileName, 'full') : props.filePath) : null"
    :style="{ minWidth: props.preloaderMinWidth && !isImageLoaded ? props.preloaderMinWidth : 'unset' }"
  >
    <!-- Spinner overlay -->
    <div v-if="showPlaceholder" class="spinner-overlay file-thumbnail__spinner-overlay">
      <!-- only show spinner if a filename was provided, it it wasn't provided that means just show a placeholder -->
      <img v-if="fileName && props.isWithLoader" class="spinner-overlay__spinner" src="/images/thumbnails_loader7.gif" alt="loader" />

      <div v-if="isVideoFileType" class="spinner-overlay__note-text">Processing Video...</div>
      <div class="spinner-overlay__bg-wrap">
        <img v-if="_placeholderType === 'video'" class="spinner-overlay__bg" :src="iconVideoPlaceholder" alt="Video placeholder" draggable="false" />
        <img v-else class="spinner-overlay__bg" :src="iconImagePlaceholder" alt="Image placeholder" draggable="false" />
      </div>
    </div>
    <!-- / Spinner overlay -->

    <div class="file-thumbnail__video-icon-wrap">
      <IconEmbedded v-if="isVideoFileType" class="file-thumbnail__video-icon" name="video_1-5" :size="44" color="rgba(255, 255, 255, 1)" />
    </div>

    <!-- Checkbox & highlighting -->
    <label
      v-if="Object.keys(slots).length && !props.isRemoveButton && !props.isDeleteButton"
      :class="{
        'checkbox-n-highlighting': true,
        'file-thumbnail__checkbox-n-highlighting': true,
        'file-thumbnail__checkbox-n-highlighting--covers-entire-thumbnail-area': props.isEntireAreaCheckable,
      }"
      @click.stop
    >
      <span class="checkbox-n-highlighting__in-wrap">
        <slot />
        <span class="checkbox-n-highlighting__checkbox-imitation">
          <IconEmbedded name="check_5" :size="13" />
        </span>
        <span class="checkbox-n-highlighting__highlighting-overlay"></span>
      </span>
    </label>
    <!-- / Checkbox & highlighting -->

    <ButtonIconExpandable
      v-if="!props.isEntireAreaClickable && props.galleryPostfix && !props.isRemoveButton && !props.isDeleteButton"
      class="file-thumbnail__fancybox-button"
      :data-fancybox="`gallery${props.galleryPostfix}`"
      tag="a"
      :href="props.fileName ? generateSrc(props.fileName, 'full') : props.filePath"
    >
      <img style="display: none" :src="props.fileName ? generateSrc(props.fileName, 'thumb-tiny') : props.filePath" alt="image" />
    </ButtonIconExpandable>

    <CloseDeleteButton v-if="props.isRemoveButton && isImageLoaded" class="file-thumbnail__remove-button" @click="emit('remove', props.fileName || props.filePath)" size="tiny" color="black" />

    <CloseDeleteButton
      v-if="props.isDeleteButton && isImageLoaded"
      class="file-thumbnail__delete-button"
      @click="emit('delete', props.fileName || props.filePath)"
      size="tiny"
      icon="trashcan"
      color="red"
    />
    <!-- only make attempts to render the img tag if a filename is actually provided -->
    <template v-if="props.fileName || props.filePath">
      <div v-if="props.isFixedAspectRatio || !isImageLoaded" class="file-thumbnail__thumbnail-wrap">
        <img
          class="file-thumbnail__img"
          :style="{ objectFit: props.imageFit }"
          :src="(props.fileName ? generateSrc(props.fileName, thumbSize) : props.filePath) + imageUrlQueryTimestamp"
          :alt="props.fileName || props.filePath"
          draggable="false"
          @load="
            () => {
              isImageLoaded = true;
              emit('imageLoad');
            }
          "
          @error="handleImageLoadError"
          @click="$event => emit('clickOnImage', $event)"
        />
      </div>
      <div v-else class="file-thumbnail__bg-image-wrap">
        <img
          class="file-thumbnail__bg-image"
          :src="(props.fileName ? generateSrc(props.fileName, thumbSize) : props.filePath) + imageUrlQueryTimestamp"
          :alt="props.fileName || props.filePath"
          @load="
            () => {
              isImageLoaded = true;
              emit('imageLoad');
            }
          "
          @error="handleImageLoadError"
          draggable="false"
          @click="$event => emit('clickOnImage', $event)"
        />
      </div>
    </template>
  </component>
</template>

<script setup lang="ts">
import { ref, useSlots, computed } from "vue";
import FileUtils from "@logic/FileUtils";
import generateSrc, { ThumbSize } from "@helpers/GenerateGallerySrc";

// Components
import CloseDeleteButton from "@components/ui/CloseDeleteButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";

// Icons
import iconImagePlaceholder from "@assets/icons/image_placeholder.svg";
import iconVideoPlaceholder from "@assets/icons/video_placeholder.svg";
import ButtonIconExpandable from "@components/ui/ButtonIconExpandable.vue";

const emit = defineEmits<{
  (e: "remove", id: string): void;
  (e: "delete", id: string): void;
  (e: "imageLoad"): void;
  (e: "clickOnImage", event: Event): void;
}>();

const props = withDefaults(
  defineProps<{
    fileName?: string | null; // automatically generates the thumbnails and the full size image uri for the fancybox
    filePath?: string | null;
    galleryPostfix?: string;
    placeholderType?: "image" | "video";
    isRemoveButton?: boolean;
    isDeleteButton?: boolean;
    isFixedAspectRatio?: boolean;
    thumbSize?: ThumbSize;
    imageFit?: "contain" | "cover";
    isEntireAreaClickable?: boolean;
    isEntireAreaCheckable?: boolean;
    preloaderMinWidth?: string; // when the thumbnail is used inside a flex container, it's width shrinks by ~10px (when the image is not loaded yet), which looks ugly. This prop prevents such shrinking
    isWithLoader?: boolean;
  }>(),
  {
    fileName: "",
    filePath: "",
    galleryPostfix: "",
    placeholderType: undefined,
    isRemoveButton: false,
    isDeleteButton: false,
    isFixedAspectRatio: true,
    // Note: We should likely default to a smaller size and have the few locations that need larger sizes pass it in
    thumbSize: "thumb-large",
    imageFit: "contain",
    isEntireAreaClickable: false,
    isEntireAreaCheckable: false,
    preloaderMinWidth: "",
    isWithLoader: true,
  }
);

const slots = useSlots();

// Check isVideo ==============================================================
const isVideoFileType = computed(() => FileUtils.isVideoFileType(props.fileName || props.filePath, null));

const showPlaceholder = computed(() => !isImageLoaded.value || (!props.fileName && !props.filePath));
const _placeholderType = computed<"image" | "video">(() => props.placeholderType || (isVideoFileType.value ? "video" : "image"));

// Handle image load error ====================================================
const isImageLoaded = ref(false);
const imageUrlQueryTimestamp = ref("");
const failedCount = ref(0);
function handleImageLoadError() {
  isImageLoaded.value = false;
  failedCount.value += 1;

  const timeoutMs = 5000;
  // 60 attempts allowed (that's 5 minutes of attempts every 5 seconds)
  // this is to allow videos enough time to process and be available
  if (failedCount.value < 60) {
    setTimeout(() => (imageUrlQueryTimestamp.value = `?t=${new Date().getTime()}`), timeoutMs);
  }
}
</script>

<style scoped lang="scss">
@use "sass:math";
@import "@/scss/variables.scss";

// Checkbox & highlighting ====================================================
.checkbox-n-highlighting {
  width: 0;
  height: 0;
  display: block;

  &__in-wrap {
    display: block;
  }

  :deep(input[type="checkbox"]) {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  &__checkbox-imitation {
    width: 31px;
    height: 31px;
    margin: 5px auto auto 5px;
    border-radius: 3px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    z-index: 5;
    color: transparent;
    background: rgba(255, 255, 255, 0.45);
    transition: background-color 0.07s ease-in-out;
    //Caused issues when lots of photos: backdrop-filter: blur(10px);

    &::after {
      content: "";
      width: 15px;
      height: 15px;
      box-sizing: border-box;
      border: 1px #c1c1c1 solid;
      border-radius: 3px;
      display: flex;
      justify-content: center;
      align-items: center;
      position: absolute;
      inset: 8px auto auto 8px;
      z-index: -1;
      background: #fff;
    }

    &:hover {
      background: rgba(255, 255, 255, 0.65);
    }
  }
  :deep(input[type="checkbox"]) + &__checkbox-imitation {
    svg {
      fill: rgba(0, 0, 0, 0);
    }
  }
  :deep(input[type="checkbox"]:checked) + &__checkbox-imitation {
    svg {
      fill: rgba(0, 0, 0, 1);
    }
  }

  &__highlighting-overlay {
    width: 100%;
    height: 100%;
    border-radius: 6px;
    outline: 4px #118689 solid;
    outline-offset: -2px;
    position: absolute;
    inset: 0 auto auto 0;
    z-index: 4;
    opacity: 0;
    background: rgba(17, 134, 137, 0.35);
    pointer-events: none;
  }
  :deep(input[type="checkbox"]:checked) + &__checkbox-imitation + &__highlighting-overlay {
    opacity: 1;
  }
}

// Spinner overlay ============================================================
.spinner-overlay {
  width: 100%;
  height: 100%;
  border-radius: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 0;
  background: #d7d7d7;

  &__spinner {
    width: 40px;
    height: 40px;
    opacity: 0.7;
  }

  &__note-text {
    @include standardFont;
    padding: 0 10px;
    text-align: center;
    text-wrap: balance;
  }

  &__bg-wrap {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    inset: 0 auto auto 0;
    z-index: -1;
  }

  &__bg {
    width: 60%;
    height: 60%;
    opacity: 0.07;
  }
}

// File thumbnail =============================================================
.file-thumbnail {
  border-radius: 6px;
  display: flex;
  align-items: center;
  position: relative;
  z-index: 0;
  user-select: none;

  &--not-clickable {
    pointer-events: none;
  }

  &--fixed-aspect-ratio {
    aspect-ratio: 4/3;
  }

  &__spinner-overlay {
    position: absolute;
    inset: 0 auto auto 0;
    z-index: 7;
  }

  &__video-icon-wrap {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    inset: 0 auto auto 0;
    z-index: 4;
    pointer-events: none;
  }

  &__video-icon {
    width: 44px;
    min-width: 22px;
    max-width: calc(100% - 12px);
    max-height: calc(100% - 12px);
    aspect-ratio: 1/1;
    opacity: 1;
  }

  &__checkbox-n-highlighting {
    align-self: flex-start;
    justify-self: flex-start;
    position: relative;
    z-index: 8;

    &--covers-entire-thumbnail-area {
      width: 100%;
      height: 100%;
    }
  }

  &__fancybox-button {
    position: absolute;
    inset: 5px 5px auto auto;
    z-index: 9;
  }

  &__remove-button,
  &__delete-button {
    position: absolute;
    inset: -3px -3px auto auto;
    z-index: 5 !important;
  }

  &__thumbnail-wrap {
    width: 100%;
    height: 100%;
    border-radius: 6px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    inset: 0 auto auto 0;
    z-index: -1;
    overflow: hidden;
    //Caused issues when lots of photos: backdrop-filter: blur(15px);
    will-change: filter;
    background: #dadada;
  }

  &__img {
    width: 100%;
    height: 100%;
  }

  &__bg-image-wrap {
    width: 100%;
    height: 100%;
    border-radius: 6px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    overflow: hidden;
  }

  &__bg-image {
    width: 100%;
    height: 100%;
    border-radius: 6px;
    object-fit: cover;
    will-change: filter;
  }
}
</style>
